import { z } from 'zod';

export const PC_FACTS_ATTR = 'data-pc-facts';

export const FactStatusSchema = z.enum([
  'sourced',
  'derived',
  'illustrative',
  'unverified',
]);
export type FactStatus = z.infer<typeof FactStatusSchema>;

export const WebFactSourceSchema = z.object({
  kind: z.literal('web'),
  url: z.string().url(),
  title: z.string().optional(),
  publisher: z.string().optional(),
  publishedAt: z.string().optional(),
  accessedAt: z.string(),
});

export const SpaceFileFactSourceSchema = z.object({
  kind: z.literal('space_file'),
  fileId: z.string(),
  fileName: z.string(),
  page: z.number().int().positive().optional(),
});

export const WorkbookFactSourceSchema = z.object({
  kind: z.literal('workbook'),
  workbookId: z.string(),
  sheetId: z.string(),
  range: z.string(),
});

export const FactSourceSchema = z.discriminatedUnion('kind', [
  WebFactSourceSchema,
  SpaceFileFactSourceSchema,
  WorkbookFactSourceSchema,
]);
export type FactSource = z.infer<typeof FactSourceSchema>;

export const FactBasisSchema = z.object({
  unit: z.string().optional(),
  period: z.string().optional(),
  scope: z.string().optional(),
});
export type FactBasis = z.infer<typeof FactBasisSchema>;

export const FactIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{1,32}$/, 'fact ids are short slugs, e.g. f1');

export const FactSchema = z
  .object({
    id: FactIdSchema,
    claim: z.string().min(1).max(500),
    value: z.string().max(120).optional(),
    basis: FactBasisSchema.optional(),
    status: FactStatusSchema,
    source: FactSourceSchema.optional(),
    excerpt: z.string().max(2000).optional(),
    derivation: z
      .object({
        inputs: z.array(FactIdSchema).min(1),
        formula: z.string().min(1).max(300),
      })
      .optional(),
    recordedAt: z.string(),
    runId: z.string(),
  })
  .superRefine((fact, ctx) => {
    if (fact.status === 'sourced' && (!fact.source || !fact.excerpt)) {
      ctx.addIssue({
        code: 'custom',
        message: `fact ${fact.id}: a sourced fact needs both a source and a verbatim excerpt`,
      });
    }
    if (fact.status === 'derived' && !fact.derivation) {
      ctx.addIssue({
        code: 'custom',
        message: `fact ${fact.id}: a derived fact needs a derivation (inputs + formula)`,
      });
    }
  });
export type Fact = z.infer<typeof FactSchema>;

export const SlideFactsSchema = z
  .object({
    version: z.literal(1),
    facts: z.array(FactSchema),
  })
  .superRefine((value, ctx) => {
    const ids = new Set<string>();
    for (const fact of value.facts) {
      if (ids.has(fact.id)) {
        ctx.addIssue({ code: 'custom', message: `duplicate fact id ${fact.id}` });
      }
      ids.add(fact.id);
    }
    for (const fact of value.facts) {
      for (const input of fact.derivation?.inputs ?? []) {
        if (!ids.has(input)) {
          ctx.addIssue({
            code: 'custom',
            message: `fact ${fact.id} derives from unknown fact ${input}`,
          });
        }
      }
    }
  });
export type SlideFacts = z.infer<typeof SlideFactsSchema>;

export function parseFactsAttribute(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(/[\s,]+/)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}
